import { StackScreenProps } from "@react-navigation/stack";
import React from "react";
import { StockOrderRootParamList } from "../../../../types";
import {
  Button,
  DEFAULT_PAGE_WIDTH,
  View,
  useThemeProvider,
  Page,
  SearchBar,
  TextButton,
} from "@towersystems/roam-common-native";
import { SceneMap, TabView } from "react-native-tab-view";
import { OtherStockOrdersList, PendingStockOrdersList } from "./components";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { Spacings } from "react-native-ui-lib";
import { useWindowDimensions } from "react-native";
import { useTranslation } from "../../../../utilities/i18n";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { StockOrderFilterValues } from "../../types";
import { useFocusEffect } from "@react-navigation/native";
import { AntDesign } from "@expo/vector-icons";
import { renderTabBar } from "../../../../components/TabBar";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";

const renderScene = SceneMap({
  pending: PendingStockOrdersList,
  other: OtherStockOrdersList,
});

export type StockOrderListScreenProps = StackScreenProps<
  StockOrderRootParamList,
  "StockOrderListScreen"
>;

export const StockOrderListScreen = ({
  navigation,
}: StockOrderListScreenProps) => {
  const [index, setIndex] = React.useState(0);
  const layout = useWindowDimensions();
  const search = useSearch();
  const { t } = useTranslation("stockorder");
  const { filterState } = useListFilterContext<StockOrderFilterValues>();
  const transitionEnded = useHasTransitionEnded(navigation);
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });

  const { activeFiltersCount } = filterState;

  const pendingTitle = t("tabs.in_progress");
  const otherTitle = t("tabs.other");

  const [routes, setRoutes] = React.useState([
    { key: "pending", title: pendingTitle },
    { key: "other", title: otherTitle },
  ]);

  React.useEffect(() => {
    setRoutes([
      { key: "pending", title: pendingTitle },
      { key: "other", title: otherTitle },
    ]);
  }, [pendingTitle, otherTitle]);

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.create")}
          isAction
          onPress={() =>
            navigation.navigate("CreateStockOrderRootScreen", {
              screen: "CreateStockOrderScreen",
            })
          }
          size="compact"
        ></Button>
      ),
    });
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("StockOrderFilterRootScreen", {
                  screen: "StockOrderFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount])
  );

  if (false == transitionEnded) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  const maxWidth =
    dimensions.width > DEFAULT_PAGE_WIDTH
      ? DEFAULT_PAGE_WIDTH
      : dimensions.width;

  const tabContent =
    dimensions.width > 0 ? (
      <TabView
        renderTabBar={renderTabBar}
        navigationState={{ index, routes }}
        renderScene={renderScene}
        onIndexChange={setIndex}
        initialLayout={{
          width: maxWidth,
        }}
        style={{
          maxWidth: maxWidth,
          alignSelf: "center",
          width: "100%",
        }}
      />
    ) : undefined;

  return (
    <Page padding={0} fullWidth scrollable={false}>
      <SearchContext.Provider value={search}>
        <View
          style={{
            marginTop: Spacings.s6,
            maxWidth: DEFAULT_PAGE_WIDTH,
            width: "100%",
            alignSelf: "center",
            paddingHorizontal: Spacings.s6,
          }}
          onLayout={(event) => {
            const { width, height } = event.nativeEvent.layout;
            setDimensions({ width, height });
          }}
        >
          <SearchBar
            placeholder={`${t("common:search")}...`}
            value={search.query}
            onChange={search.setQuery}
          />
        </View>
        {tabContent}
      </SearchContext.Provider>
    </Page>
  );
};
